import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";

const DICT = {
    en: {
        // Nav / top-level
        "nav.dashboard": "Library",
        "nav.settings": "Settings",
        "nav.pricing": "Pricing",
        "nav.signOut": "Sign out",
        // Settings page
        "settings.title": "Settings",
        "settings.language.title": "Language",
        "settings.language.subtitle": "Choose your app & AI language",
        "settings.language.english": "English",
        "settings.language.spanish": "Español",
        "settings.ai.title": "AI Model",
        "settings.ai.subtitle": "Model used for drafting, editing & proofreading",
        "settings.ai.change": "Change",
        "settings.writing.title": "Writing Mode",
        "settings.writing.subtitle.default": "Standard editor",
        "settings.writing.subtitle.distraction": "Distraction-free editor with 1.5× line height",
        "settings.writing.toggle": "Distraction-free",
        "settings.sync.title": "Sync",
        "settings.sync.subtitle": "Your work syncs across web & mobile",
        "settings.privacy.title": "Privacy Policy",
        "settings.privacy.subtitle": "How we handle your data",
        "settings.cancel.title": "Cancel Subscription",
        "settings.cancel.subtitle": "Ends at the close of your paid period",
        "settings.cancel.button": "Cancel subscription",
        "settings.cancel.confirmTitle": "Cancel your subscription?",
        "settings.cancel.confirmBody": "Your {plan} benefits stay active until your paid period ends. After that, you'll automatically move to Seer (free). You won't be charged again.",
        "settings.cancel.confirmYes": "Yes, cancel",
        "settings.cancel.confirmNo": "Keep my subscription",
        "settings.cancel.scheduled.title": "Cancellation scheduled",
        "settings.cancel.scheduled.subtitle": "{plan} stays active until {date} — then Seer",
        "settings.cancel.resume": "Resume subscription",
        "settings.signOut": "Sign Out",
        "settings.deleteAccount": "Delete Account",
        "settings.deleteAccount.confirmTitle": "Delete your account?",
        "settings.deleteAccount.confirmBody": "Your account and every novel, chapter, character, plot beat, cover, and background you own will be hidden immediately and permanently deleted after 30 days. Sign in with the same Google account within 30 days to reactivate everything. Type DELETE below to confirm.",
        "settings.deleteAccount.confirmInput": "Type DELETE to confirm",
        "settings.deleteAccount.confirmYes": "Delete my account",
        "settings.deleteAccount.confirmNo": "Keep my account",
        // Common
        "common.back": "Back",
        "common.cancel": "Cancel",
        "common.save": "Save",
        "common.close": "Close",
        "common.add": "Add",
        "common.loading": "Loading…",

        // Dashboard
        "dash.changeImage": "Change image",
        "dash.eyebrow": "Novelist · Editor · Proofreader",
        "dash.heroLine1": "Write the novel",
        "dash.heroLine2": "only you",
        "dash.heroLine3": "could write.",
        "dash.heroSubtitle": "An AI collaborator for drafting chapters, refining prose, and catching the line that almost worked. Build your manuscript, character bible, and plot — all in one quiet desk.",
        "dash.yourManuscripts": "Your Manuscripts",
        "dash.novel": "Novel",
        "dash.novels": "Novels",
        "dash.newNovel": "New novel",
        "dash.newNovelLocked": "New novel (locked)",
        "dash.limitReached": "Novel limit reached · upgrade to Enchanter",
        "dash.begin": "Begin a new manuscript",
        "dash.beginDesc": "You can edit any of this later.",
        "dash.field.title": "Title",
        "dash.field.genre": "Genre",
        "dash.field.pov": "POV",
        "dash.field.tone": "Tone",
        "dash.field.dailyGoal": "Daily goal (words)",
        "dash.field.synopsis": "Synopsis",
        "dash.ph.title": "e.g. The Lantern Keeper",
        "dash.ph.genre": "Literary fiction",
        "dash.ph.pov": "First person",
        "dash.ph.tone": "Melancholic, lyrical",
        "dash.ph.synopsis": "A widowed lighthouse keeper begins receiving letters from a stranger who knows what really happened the night the storm came in...",
        "dash.createNovel": "Create Novel",
        "dash.noManuscripts": "No manuscripts yet",
        "dash.startFirst": "Start your first novel — Day Dreamer will help you outline, draft, and refine.",
        "dash.startWriting": "Start writing",
        "dash.untitledGenre": "Untitled genre",
        "dash.noSynopsis": "No synopsis yet.",
        "dash.admin": "Admin",
        "dash.adminInbox": "Admin inbox",
        "dash.upgrade": "Upgrade",
        "dash.managePlan": "Manage plan",

        // Pricing
        "pricing.eyebrow": "Pricing",
        "pricing.titlePre": "Four ways to ",
        "pricing.titleEm": "dream",
        "pricing.subtitle": "Start with three novels for free. Unlock AI writing whenever the words feel stuck.",
        "pricing.freeTier": "Free tier",
        "pricing.subscription": "Subscription",
        "pricing.mostLoved": "Most loved",
        "pricing.currentPlan": "Current plan",
        "pricing.default": "Default",
        "pricing.chooseEnchanter": "Choose Enchanter · $19/mo",
        "pricing.chooseMagnus": "Choose Magnus · $39/mo",
        "pricing.chooseAza": "Choose Aza · $99/mo",
        "pricing.yearly": "Or pay yearly and save",
        "pricing.footer": "Test mode — no real charges. Powered by Stripe.",

        // Workspace — top bar
        "ws.library": "Library",
        "ws.focus": "Focus",
        "ws.exitFocus": "Exit Focus",
        "ws.saving": "Saving",
        "ws.saved": "Saved",
        "ws.sync.offline": "Offline · saved here",
        "ws.sync.limit": "Word limit · saved here",
        "ws.sync.reconnected": "Back online — syncing your changes",
        "ws.sync.restored": "Restored unsynced changes from this device",
        "ws.bottombar.chapters": "Chapters",
        "ws.bottombar.focus": "Focus",
        "ws.bottombar.tools": "Tools",
        "ws.words": "words",
        "ws.rename": "Click to rename",
        "ws.titleUpdated": "Title updated",
        "ws.chapters": "Chapters",
        "ws.chapterTitle": "Chapter title",
        "ws.beginChapter": "Begin your chapter…",
        "ws.openingManuscript": "Opening manuscript…",

        // Workspace — tabs
        "ws.tab.write": "Write",
        "ws.tab.edit": "Edit",
        "ws.tab.proof": "Proof",
        "ws.tab.cast": "Cast",
        "ws.tab.plot": "Plot",

        // Track-changes bar
        "ws.tc.pendingOne": "{n} AI change pending",
        "ws.tc.pendingMany": "{n} AI changes pending",
        "ws.tc.acceptAll": "Accept all",
        "ws.tc.rejectAll": "Reject all",
        "ws.tc.accepted": "Accepted",
        "ws.tc.rejected": "Rejected",

        // Write panel
        "ws.write.descPre": "Describe a scene to draft, or continue from where you stopped. New text appears as ",
        "ws.write.descTracked": "tracked changes",
        "ws.write.placeholder": "e.g. Open the chapter with Mira finding a letter in her father's coat pocket — rainy, dim, quiet dread.",
        "ws.write.draftScene": "Draft scene",
        "ws.write.continue": "Continue",
        "ws.write.insertChapter": "Insert into chapter",

        // Edit panel
        "ws.edit.desc": "Select text in the editor first, then choose a preset or write your own instruction.",
        "ws.edit.placeholder": "Custom instruction for the selected passage…",
        "ws.edit.rewriteSelection": "Rewrite selection",
        "ws.edit.replaceSelection": "Replace selection",
        "ws.preset.sensory": "Rewrite for stronger sensory detail",
        "ws.preset.concise": "Make this more concise",
        "ws.preset.expand": "Expand this paragraph",
        "ws.preset.pastTense": "Change to past tense",
        "ws.preset.tension": "Increase tension and pacing",
        "ws.preset.dialogue": "Improve dialogue naturalness",

        // Proofread panel
        "ws.proof.desc": "Find issues and apply suggested fixes with one click. Each fix lands as a tracked change.",
        "ws.proof.selection": "Selection",
        "ws.proof.wholeChapter": "Whole chapter",
        "ws.proof.issueOne": "issue",
        "ws.proof.issueMany": "issues",
        "ws.proof.ignored": "ignored",
        "ws.proof.showIgnored": "Show ignored",
        "ws.proof.hideIgnored": "Hide ignored",
        "ws.proof.applyAll": "Apply all",
        "ws.proof.dismiss": "Dismiss",
        "ws.proof.runToSee": "Run proofread to see suggestions here.",
        "ws.proof.allIgnoredPre": "All suggestions ignored. Inline highlights remain in your text — click ",
        "ws.proof.allIgnoredStrong": "Show ignored",
        "ws.proof.allIgnoredPost": " to revisit them.",
        "ws.proof.analyzing": "Analyzing prose…",
        "ws.proof.apply": "Apply",
        "ws.proof.ignore": "Ignore",
        "ws.proof.restore": "Restore",
        "ws.proof.remove": "(remove)",
        "ws.proof.ignoredKept": "Ignored · highlight kept in editor",
        "ws.proof.appliedTracked": "Applied as tracked change",

        // Panels (floating)

        // Cast panel
        "ws.cast.bible": "Character Bible",
        "ws.cast.newCharacter": "New character",
        "ws.cast.addToBible": "Add to your character bible.",
        "ws.cast.name": "Name",
        "ws.cast.color": "Color",
        "ws.cast.role": "Role",
        "ws.cast.rolePh": "Protagonist / Antagonist / Ally",
        "ws.cast.description": "Description",
        "ws.cast.traits": "Traits",
        "ws.cast.traitsPh": "Stoic, observant, lonely",
        "ws.cast.arc": "Arc",
        "ws.cast.noChars": "No characters yet.",
        "ws.cast.generateIdea": "Generate character idea",

        // Plot panel
        "ws.plot.beats": "Plot Beats",
        "ws.plot.newBeat": "New plot beat",
        "ws.plot.addBeat": "Add a story beat.",
        "ws.plot.title": "Title",
        "ws.plot.act": "Act",
        "ws.plot.actPh": "Act I, Midpoint, Climax…",
        "ws.plot.description": "Description",
        "ws.plot.noBeats": "No plot beats yet. Generate an outline below.",
        "ws.plot.generateOutline": "Generate full outline",
        "ws.plot.insertChapter": "Insert into chapter",

        // Scenes
        "ws.scenes": "Scenes",
        "ws.scene.add": "Add scene",
        "ws.scene.placeholder": "Scene title (e.g. Mira finds the letter)",
        "ws.scene.empty": "Plan scenes for this chapter. Drag a moment in mind, drop it here.",
        "ws.scene.edit": "Edit scene",
        "ws.scene.updateDesc": "Update title, summary, or status.",
        "ws.scene.title": "Title",
        "ws.scene.summary": "Summary",
        "ws.scene.status": "Status",
        "ws.scene.todo": "To-do",
        "ws.scene.draft": "Drafted",
        "ws.scene.done": "Done",

        // Chapter meta bar
        "ws.meta.minRead": "min read",
        "ws.meta.editGoal": "Edit goal",
        "ws.meta.addGoal": "+ Chapter goal",
        "ws.meta.set": "Set",

        // Share dialog
        "ws.share.button": "Share",
        "ws.share.title": "Share with beta readers",
        "ws.share.desc": "Anyone with this link can read your manuscript and leave notes. No account required.",
        "ws.share.copy": "Copy",
        "ws.share.copied": "Copied to clipboard",
        "ws.share.openPreview": "Open reader preview",
        "ws.share.revoke": "Revoke link",
        "ws.share.generateDesc": "Generate a public read-only link to send to beta readers.",
        "ws.share.generate": "Generate share link",
        "ws.share.enabled": "Share link enabled",
        "ws.share.revoked": "Share link revoked",

        // Notes dialog
        "ws.notes.button": "Notes",
        "ws.notes.title": "Beta reader notes",
        "ws.notes.desc": "Notes left by people you sent the share link to. Anchored quotes show exactly where the feedback lands.",
        "ws.notes.empty": "No notes yet. Share your manuscript and they'll appear here.",

        // Cover dialog
        "ws.cover.cover": "Cover",
        "ws.cover.addCover": "Add cover",
        "ws.cover.title": "Cover art",
        "ws.cover.desc": "Upload your own image for the cover.",
        "ws.cover.upload": "Upload image",
        "ws.cover.remove": "Remove cover",
        "ws.cover.formats": "JPG, PNG, or WEBP · max 8 MB",

        // Export
        "ws.export.button": "Export",

        // Versions dialog
        "ws.versions.button": "Versions",
        "ws.versions.title": "Chapter version history",
        "ws.versions.desc": "Each save creates a snapshot. Last 30 are kept.",
        "ws.versions.none": "No versions yet.",
        "ws.versions.selectPreview": "Select a version to preview.",
        "ws.versions.restoreThis": "Restore this version",
        "ws.versions.wordsBy": "words ·",

        // Reader (public)
        "rd.loading": "Loading manuscript…",
        "rd.invalid": "This reading link is invalid or has been revoked by the author.",
        "rd.readonly": "Read-only preview",
        "rd.notes": "Notes",
        "rd.chapters": "Chapters",
        "rd.selectHint": "Select any passage to leave the author a note right where it belongs.",
        "rd.emptyChapter": "This chapter is empty.",
        "rd.synopsis": "Synopsis",
        "rd.prev": "← Previous",
        "rd.next": "Next →",
        "rd.chapterOf": "Chapter {n} of {total}",
        "rd.swipeHint": "swipe to turn pages",
        "rd.noChapters": "No chapters available yet.",
        "rd.addNoteHere": "Add note here",
        "rd.notesFor": "Notes for",
        "rd.thisManuscript": "this manuscript",
        "rd.notesEmpty": "No notes yet. Select a passage in the chapter and leave the first thought for the author.",
        "rd.notOnView": "This passage isn't on the current view",
        "rd.yourName": "Your name",
        "rd.notePhAnchor": "Your note on the selected passage…",
        "rd.notePhChapter": "Your note for this chapter…",
        "rd.send": "Send to author",
        "rd.needNameNote": "Add your name and a note",
        "rd.shared": "Note shared with the author",
        "rd.postFail": "Couldn't post",
    },
    es: {
        "nav.dashboard": "Biblioteca",
        "nav.settings": "Ajustes",
        "nav.pricing": "Precios",
        "nav.signOut": "Cerrar sesión",
        "settings.title": "Ajustes",
        "settings.language.title": "Idioma",
        "settings.language.subtitle": "Elige el idioma de la app y de la IA",
        "settings.language.english": "English",
        "settings.language.spanish": "Español",
        "settings.ai.title": "Modelo de IA",
        "settings.ai.subtitle": "Modelo usado para redactar, editar y corregir",
        "settings.ai.change": "Cambiar",
        "settings.writing.title": "Modo de escritura",
        "settings.writing.subtitle.default": "Editor estándar",
        "settings.writing.subtitle.distraction": "Editor sin distracciones con interlineado 1,5×",
        "settings.writing.toggle": "Sin distracciones",
        "settings.sync.title": "Sincronización",
        "settings.sync.subtitle": "Tu trabajo se sincroniza en web y móvil",
        "settings.privacy.title": "Política de privacidad",
        "settings.privacy.subtitle": "Cómo tratamos tus datos",
        "settings.cancel.title": "Cancelar suscripción",
        "settings.cancel.subtitle": "Se cancela al final del período pagado",
        "settings.cancel.button": "Cancelar suscripción",
        "settings.cancel.confirmTitle": "¿Cancelar tu suscripción?",
        "settings.cancel.confirmBody": "Los beneficios de {plan} siguen activos hasta que termine tu período pagado. Después pasarás automáticamente a Seer (gratis). No se te cobrará de nuevo.",
        "settings.cancel.confirmYes": "Sí, cancelar",
        "settings.cancel.confirmNo": "Mantener mi suscripción",
        "settings.cancel.scheduled.title": "Cancelación programada",
        "settings.cancel.scheduled.subtitle": "{plan} sigue activo hasta el {date} — luego Seer",
        "settings.cancel.resume": "Reanudar suscripción",
        "settings.signOut": "Cerrar sesión",
        "settings.deleteAccount": "Eliminar cuenta",
        "settings.deleteAccount.confirmTitle": "¿Eliminar tu cuenta?",
        "settings.deleteAccount.confirmBody": "Tu cuenta y todas tus novelas, capítulos, personajes, tramas, portadas y fondos se ocultarán de inmediato y se eliminarán permanentemente tras 30 días. Inicia sesión con la misma cuenta de Google dentro de 30 días para reactivar todo. Escribe DELETE abajo para confirmar.",
        "settings.deleteAccount.confirmInput": "Escribe DELETE para confirmar",
        "settings.deleteAccount.confirmYes": "Eliminar mi cuenta",
        "settings.deleteAccount.confirmNo": "Mantener mi cuenta",
        "common.back": "Atrás",
        "common.cancel": "Cancelar",
        "common.save": "Guardar",
        "common.close": "Cerrar",
        "common.add": "Añadir",
        "common.loading": "Cargando…",

        // Dashboard
        "dash.changeImage": "Cambiar imagen",
        "dash.eyebrow": "Novelista · Editor · Corrector",
        "dash.heroLine1": "Escribe la novela",
        "dash.heroLine2": "que solo tú",
        "dash.heroLine3": "podrías escribir.",
        "dash.heroSubtitle": "Un colaborador de IA para redactar capítulos, pulir la prosa y detectar esa frase que casi funcionaba. Construye tu manuscrito, tu biblia de personajes y tu trama — todo en un mismo escritorio tranquilo.",
        "dash.yourManuscripts": "Tus manuscritos",
        "dash.novel": "Novela",
        "dash.novels": "Novelas",
        "dash.newNovel": "Nueva novela",
        "dash.newNovelLocked": "Nueva novela (bloqueada)",
        "dash.limitReached": "Límite de novelas alcanzado · mejora a Enchanter",
        "dash.begin": "Comienza un nuevo manuscrito",
        "dash.beginDesc": "Puedes editar todo esto más tarde.",
        "dash.field.title": "Título",
        "dash.field.genre": "Género",
        "dash.field.pov": "Punto de vista",
        "dash.field.tone": "Tono",
        "dash.field.dailyGoal": "Meta diaria (palabras)",
        "dash.field.synopsis": "Sinopsis",
        "dash.ph.title": "p. ej. El guardián del faro",
        "dash.ph.genre": "Ficción literaria",
        "dash.ph.pov": "Primera persona",
        "dash.ph.tone": "Melancólico, lírico",
        "dash.ph.synopsis": "Un farero viudo comienza a recibir cartas de un desconocido que sabe lo que realmente pasó la noche en que llegó la tormenta...",
        "dash.createNovel": "Crear novela",
        "dash.noManuscripts": "Aún no hay manuscritos",
        "dash.startFirst": "Empieza tu primera novela — Day Dreamer te ayudará a esbozar, redactar y pulir.",
        "dash.startWriting": "Empezar a escribir",
        "dash.untitledGenre": "Género sin título",
        "dash.noSynopsis": "Aún sin sinopsis.",
        "dash.admin": "Admin",
        "dash.adminInbox": "Bandeja de admin",
        "dash.upgrade": "Mejorar plan",
        "dash.managePlan": "Gestionar plan",

        // Pricing
        "pricing.eyebrow": "Precios",
        "pricing.titlePre": "Cuatro formas de ",
        "pricing.titleEm": "soñar",
        "pricing.subtitle": "Empieza con tres novelas gratis. Desbloquea la escritura con IA cuando las palabras se atasquen.",
        "pricing.freeTier": "Plan gratuito",
        "pricing.subscription": "Suscripción",
        "pricing.mostLoved": "El favorito",
        "pricing.currentPlan": "Plan actual",
        "pricing.default": "Predeterminado",
        "pricing.chooseEnchanter": "Elegir Enchanter · $19/mes",
        "pricing.chooseMagnus": "Elegir Magnus · $39/mes",
        "pricing.chooseAza": "Elegir Aza · $99/mes",
        "pricing.yearly": "O paga anual y ahorra",
        "pricing.footer": "Modo de prueba — sin cargos reales. Con tecnología de Stripe.",

        // Workspace — top bar
        "ws.library": "Biblioteca",
        "ws.focus": "Concentración",
        "ws.exitFocus": "Salir",
        "ws.saving": "Guardando",
        "ws.saved": "Guardado",
        "ws.sync.offline": "Sin conexión · guardado aquí",
        "ws.sync.limit": "Límite de palabras · guardado aquí",
        "ws.sync.reconnected": "De nuevo en línea — sincronizando tus cambios",
        "ws.sync.restored": "Se restauraron cambios sin sincronizar de este dispositivo",
        "ws.bottombar.chapters": "Capítulos",
        "ws.bottombar.focus": "Concentración",
        "ws.bottombar.tools": "Herramientas",
        "ws.words": "palabras",
        "ws.rename": "Haz clic para renombrar",
        "ws.titleUpdated": "Título actualizado",
        "ws.chapters": "Capítulos",
        "ws.chapterTitle": "Título del capítulo",
        "ws.beginChapter": "Comienza tu capítulo…",
        "ws.openingManuscript": "Abriendo manuscrito…",

        // Workspace — tabs
        "ws.tab.write": "Escribir",
        "ws.tab.edit": "Editar",
        "ws.tab.proof": "Corregir",
        "ws.tab.cast": "Elenco",
        "ws.tab.plot": "Trama",

        // Track-changes bar
        "ws.tc.pendingOne": "{n} cambio de IA pendiente",
        "ws.tc.pendingMany": "{n} cambios de IA pendientes",
        "ws.tc.acceptAll": "Aceptar todo",
        "ws.tc.rejectAll": "Rechazar todo",
        "ws.tc.accepted": "Aceptado",
        "ws.tc.rejected": "Rechazado",

        // Write panel
        "ws.write.descPre": "Describe una escena para redactar, o continúa desde donde lo dejaste. El texto nuevo aparece como ",
        "ws.write.descTracked": "cambios rastreados",
        "ws.write.placeholder": "p. ej. Abre el capítulo con Mira encontrando una carta en el bolsillo del abrigo de su padre — lluvioso, tenue, con un temor silencioso.",
        "ws.write.draftScene": "Redactar escena",
        "ws.write.continue": "Continuar",
        "ws.write.insertChapter": "Insertar en el capítulo",

        // Edit panel
        "ws.edit.desc": "Selecciona texto en el editor primero, luego elige un ajuste predefinido o escribe tu propia instrucción.",
        "ws.edit.placeholder": "Instrucción personalizada para el pasaje seleccionado…",
        "ws.edit.rewriteSelection": "Reescribir selección",
        "ws.edit.replaceSelection": "Reemplazar selección",
        "ws.preset.sensory": "Reescribir con más detalle sensorial",
        "ws.preset.concise": "Hacerlo más conciso",
        "ws.preset.expand": "Ampliar este párrafo",
        "ws.preset.pastTense": "Cambiar a tiempo pasado",
        "ws.preset.tension": "Aumentar la tensión y el ritmo",
        "ws.preset.dialogue": "Mejorar la naturalidad del diálogo",

        // Proofread panel
        "ws.proof.desc": "Encuentra problemas y aplica las correcciones sugeridas con un clic. Cada corrección aparece como un cambio rastreado.",
        "ws.proof.selection": "Selección",
        "ws.proof.wholeChapter": "Capítulo completo",
        "ws.proof.issueOne": "problema",
        "ws.proof.issueMany": "problemas",
        "ws.proof.ignored": "ignorados",
        "ws.proof.showIgnored": "Mostrar ignorados",
        "ws.proof.hideIgnored": "Ocultar ignorados",
        "ws.proof.applyAll": "Aplicar todo",
        "ws.proof.dismiss": "Descartar",
        "ws.proof.runToSee": "Ejecuta la corrección para ver sugerencias aquí.",
        "ws.proof.allIgnoredPre": "Todas las sugerencias ignoradas. Los resaltados en línea permanecen en tu texto — haz clic en ",
        "ws.proof.allIgnoredStrong": "Mostrar ignorados",
        "ws.proof.allIgnoredPost": " para volver a revisarlas.",
        "ws.proof.analyzing": "Analizando la prosa…",
        "ws.proof.apply": "Aplicar",
        "ws.proof.ignore": "Ignorar",
        "ws.proof.restore": "Restaurar",
        "ws.proof.remove": "(eliminar)",
        "ws.proof.ignoredKept": "Ignorado · resaltado conservado en el editor",
        "ws.proof.appliedTracked": "Aplicado como cambio rastreado",

        // Panels (floating)

        // Cast panel
        "ws.cast.bible": "Biblia de Personajes",
        "ws.cast.newCharacter": "Nuevo personaje",
        "ws.cast.addToBible": "Añadir a tu biblia de personajes.",
        "ws.cast.name": "Nombre",
        "ws.cast.color": "Color",
        "ws.cast.role": "Rol",
        "ws.cast.rolePh": "Protagonista / Antagonista / Aliado",
        "ws.cast.description": "Descripción",
        "ws.cast.traits": "Rasgos",
        "ws.cast.traitsPh": "Estoico, observador, solitario",
        "ws.cast.arc": "Arco",
        "ws.cast.noChars": "Aún no hay personajes.",
        "ws.cast.generateIdea": "Generar idea de personaje",

        // Plot panel
        "ws.plot.beats": "Momentos de Trama",
        "ws.plot.newBeat": "Nuevo momento de trama",
        "ws.plot.addBeat": "Añade un momento de la historia.",
        "ws.plot.title": "Título",
        "ws.plot.act": "Acto",
        "ws.plot.actPh": "Acto I, Punto medio, Clímax…",
        "ws.plot.description": "Descripción",
        "ws.plot.noBeats": "Aún no hay momentos de trama. Genera un esquema abajo.",
        "ws.plot.generateOutline": "Generar esquema completo",
        "ws.plot.insertChapter": "Insertar en el capítulo",

        // Scenes
        "ws.scenes": "Escenas",
        "ws.scene.add": "Añadir escena",
        "ws.scene.placeholder": "Título de escena (p. ej. Mira encuentra la carta)",
        "ws.scene.empty": "Planifica las escenas de este capítulo. Piensa un momento y suéltalo aquí.",
        "ws.scene.edit": "Editar escena",
        "ws.scene.updateDesc": "Actualiza el título, el resumen o el estado.",
        "ws.scene.title": "Título",
        "ws.scene.summary": "Resumen",
        "ws.scene.status": "Estado",
        "ws.scene.todo": "Pendiente",
        "ws.scene.draft": "Borrador",
        "ws.scene.done": "Hecho",

        // Chapter meta bar
        "ws.meta.minRead": "min de lectura",
        "ws.meta.editGoal": "Editar meta",
        "ws.meta.addGoal": "+ Meta del capítulo",
        "ws.meta.set": "Fijar",

        // Share dialog
        "ws.share.button": "Compartir",
        "ws.share.title": "Compartir con lectores beta",
        "ws.share.desc": "Cualquiera con este enlace puede leer tu manuscrito y dejar notas. No se necesita cuenta.",
        "ws.share.copy": "Copiar",
        "ws.share.copied": "Copiado al portapapeles",
        "ws.share.openPreview": "Abrir vista de lector",
        "ws.share.revoke": "Revocar enlace",
        "ws.share.generateDesc": "Genera un enlace público de solo lectura para enviar a lectores beta.",
        "ws.share.generate": "Generar enlace para compartir",
        "ws.share.enabled": "Enlace para compartir activado",
        "ws.share.revoked": "Enlace para compartir revocado",

        // Notes dialog
        "ws.notes.button": "Notas",
        "ws.notes.title": "Notas de lectores beta",
        "ws.notes.desc": "Notas dejadas por las personas a quienes enviaste el enlace. Las citas ancladas muestran exactamente dónde cae el comentario.",
        "ws.notes.empty": "Aún no hay notas. Comparte tu manuscrito y aparecerán aquí.",

        // Cover dialog
        "ws.cover.cover": "Portada",
        "ws.cover.addCover": "Añadir portada",
        "ws.cover.title": "Arte de portada",
        "ws.cover.desc": "Sube tu propia imagen para la portada.",
        "ws.cover.upload": "Subir imagen",
        "ws.cover.remove": "Eliminar portada",
        "ws.cover.formats": "JPG, PNG o WEBP · máx. 8 MB",

        // Export
        "ws.export.button": "Exportar",

        // Versions dialog
        "ws.versions.button": "Versiones",
        "ws.versions.title": "Historial de versiones del capítulo",
        "ws.versions.desc": "Cada guardado crea una instantánea. Se conservan las últimas 30.",
        "ws.versions.none": "Aún no hay versiones.",
        "ws.versions.selectPreview": "Selecciona una versión para previsualizar.",
        "ws.versions.restoreThis": "Restaurar esta versión",
        "ws.versions.wordsBy": "palabras ·",

        // Reader (public)
        "rd.loading": "Cargando manuscrito…",
        "rd.invalid": "Este enlace de lectura no es válido o el autor lo ha revocado.",
        "rd.readonly": "Vista de solo lectura",
        "rd.notes": "Notas",
        "rd.chapters": "Capítulos",
        "rd.selectHint": "Selecciona cualquier pasaje para dejar al autor una nota justo donde corresponde.",
        "rd.emptyChapter": "Este capítulo está vacío.",
        "rd.synopsis": "Sinopsis",
        "rd.prev": "← Anterior",
        "rd.next": "Siguiente →",
        "rd.chapterOf": "Capítulo {n} de {total}",
        "rd.swipeHint": "desliza para pasar de página",
        "rd.noChapters": "Aún no hay capítulos disponibles.",
        "rd.addNoteHere": "Añadir nota aquí",
        "rd.notesFor": "Notas para",
        "rd.thisManuscript": "este manuscrito",
        "rd.notesEmpty": "Aún no hay notas. Selecciona un pasaje del capítulo y deja el primer comentario para el autor.",
        "rd.notOnView": "Este pasaje no está en la vista actual",
        "rd.yourName": "Tu nombre",
        "rd.notePhAnchor": "Tu nota sobre el pasaje seleccionado…",
        "rd.notePhChapter": "Tu nota para este capítulo…",
        "rd.send": "Enviar al autor",
        "rd.needNameNote": "Añade tu nombre y una nota",
        "rd.shared": "Nota enviada al autor",
        "rd.postFail": "No se pudo publicar",
    },
};

// Backend-driven plan content (name + price + feature bullets) → Spanish.
const PLAN_ES = {
    price: {
        "Free": "Gratis",
        "$19 / month or $144 / year": "$19 / mes o $144 / año",
        "$39 / month or $288 / year": "$39 / mes o $288 / año",
        "$99 / month or $711 / year": "$99 / mes o $711 / año",
    },
    features: {
        "10,000 AI words to try, one time": "10.000 palabras de IA para probar, una sola vez",
        "Up to 3 novels, 40,000 words each": "Hasta 3 novelas de 40.000 palabras cada una",
        "Outline, character and plot tools": "Herramientas de esquema, personajes y trama",
        "Export to .docx / .epub / .pdf — always, on every plan":
            "Exportar a .docx / .epub / .pdf — siempre, en todos los planes",
        "7 days of version history": "7 días de historial de versiones",
        "50,000 AI words every month": "50.000 palabras de IA cada mes",
        "Unlimited novels": "Novelas ilimitadas",
        "AI write, edit and proofread": "Escritura, edición y corrección con IA",
        "Track changes and 90 days of version history":
            "Control de cambios y 90 días de historial de versiones",
        "3 beta-reader share links": "3 enlaces para lectores beta",
        "30 minutes of dictation": "30 minutos de dictado",
        "120,000 AI words every month": "120.000 palabras de IA cada mes",
        "Everything in Enchanter": "Todo lo de Enchanter",
        "Our most capable model for drafting": "Nuestro modelo más capaz para escribir",
        "Unlimited version history and share links":
            "Historial de versiones y enlaces para compartir ilimitados",
        "90 minutes of dictation": "90 minutos de dictado",
        "Full sync across web and mobile": "Sincronización completa entre web y móvil",
        "250,000 AI words every month": "250.000 palabras de IA cada mes",
        "Everything in Magnus": "Todo lo de Magnus",
        "Priority during peak demand": "Prioridad en momentos de alta demanda",
        "240 minutes of dictation": "240 minutos de dictado",
        "Direct input on the roadmap": "Influencia directa en la hoja de ruta",
    },
};

export function translatePlanPrice(lang, label) {
    if (lang !== "es") return label;
    return PLAN_ES.price[label] || label;
}
export function translatePlanFeature(lang, feature) {
    if (lang !== "es") return feature;
    return PLAN_ES.features[feature] || feature;
}

// Standalone translator (no React context) — used by the public Reader page,
// which manages its own language independent of the authenticated user's pref.
export function translate(lang, key, vars) {
    let str = (DICT[lang] && DICT[lang][key]) || DICT.en[key] || key;
    if (vars) {
        for (const [k, v] of Object.entries(vars)) {
            str = str.replace(new RegExp(`\\{${k}\\}`, "g"), v);
        }
    }
    return str;
}

const I18nCtx = createContext({ lang: "en", t: (k) => k, setLang: () => {} });

export function I18nProvider({ children }) {
    const { user } = useAuth();
    const [lang, setLangState] = useState(() => localStorage.getItem("dd.lang") || "en");

    // When the user object loads, adopt their preferred language.
    useEffect(() => {
        if (user?.language && user.language !== lang) {
            setLangState(user.language);
            localStorage.setItem("dd.lang", user.language);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.language]);

    const setLang = (next) => {
        setLangState(next);
        localStorage.setItem("dd.lang", next);
    };

    const value = useMemo(() => ({
        lang,
        setLang,
        t: (key, vars) => {
            let str = (DICT[lang] && DICT[lang][key]) || DICT.en[key] || key;
            if (vars) {
                for (const [k, v] of Object.entries(vars)) {
                    str = str.replace(new RegExp(`\\{${k}\\}`, "g"), v);
                }
            }
            return str;
        },
    }), [lang]);

    return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export function useTranslation() {
    return useContext(I18nCtx);
}
